import React, { useState, useMemo, useCallback, useContext, useRef, cloneElement } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { DealWidgetExcelExport } from "./tech-staffer-deal-time-log-helper";
import PropTypes from "prop-types";
import { LoginContext } from "../../../../../services/api/authentication-api";
import {
  createColumnMenu,
  formatLastUpdatedDate,
  renderlastUpdatedTooltipHeader,
  renderTooltipHeader,
  renderLabelWithInfoTooltipHeader,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks } from "../../../dashboard-util/dashboard-utils";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import TechDealTeamAvatarGroupCell from "../../tech-deal-team-avatar";
import {
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
  EditIconButton,
  flattenTechProjects,
  getDealFilteredSortedData,
  TechStafferNoDealOnFilterState,
  TechStafferNoDealOnSearchState,
  TechStafferNoDealsEmptyState,
  SeniorMembersCell,
} from "../tech-staffer-helper";
import TechStafferDealTimeLogHeader from "./tech-staffer-deal-time-log-header";
import TechStafferDealTimeLogModal from "./tech-staffer-deal-time-log-modal";
import "../tech-staffer-time-log.scss";

const TechStafferDealTimeLog = ({ techProjects = [], isStaffer = false, subTitle = "Technology" }) => {
  const user = useContext(LoginContext);
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const [dealState, setDealState] = useState(() =>
    getInitialDetailState(user?.userID, "techDealTimeLog")
  );
  const [searchValue, setSearchValue] = useState("");
  const [expandClass, setExpandClass] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const _exportRef = useRef(null);

  const flattenedData = useMemo(() => flattenTechProjects(techProjects), [techProjects]);

  const filteredData = useMemo(() => {
    let data = getDealFilteredSortedData(flattenedData, dealState.filter, dealState.sort);
    if (searchValue.trim()) {
      const lower = searchValue.trim().toLowerCase();
      data = data.filter((item) => item.ProjectName?.toLowerCase().includes(lower));
    }
    return data;
  }, [flattenedData, dealState.filter, dealState.sort, searchValue]);

  const columnMenu = createColumnMenu(flattenedData);
  const isEmpty = filteredData.length === 0;
  const isFilterEmptyState = isEmpty && (dealState.filter?.filters?.length ?? 0) > 0;
  const isSearchEmptyState = isEmpty && !isFilterEmptyState && !!searchValue.trim();
  const finalGridData = isEmpty
    ? [{ isEmptyRow: true, isFilterEmptyState, isSearchEmptyState }]
    : filteredData;

  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, dealState.filter),
    [dealState.filter]
  );

  const rowRender = useCallback(
    (trElement, props) => {
      if (props.dataItem?.isEmptyRow) return trElement;
      return cloneElement(trElement, {
        onClick: () => {
          setSelectedDeal(props.dataItem);
          setShowDealModal(true);
        },
      });
    },
    []
  );

  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setDealState(resetState);
    clearDetailStateFromStorage(user?.userID, "techDealTimeLog");
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  const expandWidget = () => {
    setExpandClass(expandClass === "" ? "widgetExpanded" : "");
  };

  return (
    <div
      className={`tech-staffer-time-log clickable-row ${expandClass} dashboard-container-shadow`}
    >
      <TechStafferDealTimeLogHeader
        isStaffer={isStaffer}
        resetDataState={resetDataState}
        dealState={dealState}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        expandWidget={expandWidget}
        expandClass={expandClass}
        onExport={() => _exportRef.current?.save()}
        subTitle={subTitle}
      />
      <div
        className="detail-grid-scroll-container energy-staffer-widgets"
        data-testid="deal-time-log-grid"
      >
        <Grid
          data={finalGridData}
          dataItemKey="ProjectId"
          sortable={true}
          sort={dealState.sort}
          filter={dealState.filter}
          onSortChange={(e) => {
            const newState = { ...dealState, sort: e.sort };
            setDealState(newState);
            saveDetailStateToStorage(user?.userID, newState, "", "techDealTimeLog");
          }}
          onFilterChange={(e) => {
            const newState = { ...dealState, filter: e.filter };
            setDealState(newState);
            saveDetailStateToStorage(user?.userID, newState, "", "techDealTimeLog");
          }}
          resizable={true}
          rowRender={rowRender}
        >
          <Column
            field="ProjectId"
            title={renderTooltipHeader("#")}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 ? (
                props.dataItem.isEmptyRow ? (
                  <td colSpan={9} className="k-table-td">
                    {isFilterEmptyState ? (
                      <TechStafferNoDealOnFilterState resetDataState={resetDataState} />
                    ) : props.dataItem.isSearchEmptyState ? (
                      <TechStafferNoDealOnSearchState searchValue={searchValue} resetDataState={resetDataState} />
                    ) : (
                      <TechStafferNoDealsEmptyState isStaffer={isStaffer} />
                    )}
                  </td>
                ) : (
                  <td className="badge-edit-cell background-white k-table-td">
                    <div className="badge-edit-content" style={{ pointerEvents: "none" }}>
                      <FluentBadge
                        shape="rounded"
                        appearance="tint"
                        size="medium"
                        color="success"
                        text={`GIB-${props.dataItem.ProjectId}`}
                      />
                      {props.dataItem.IsOverDue ? (
                        <FluentBadge
                          shape="rounded"
                          appearance="filled"
                          size="medium"
                          color="warning"
                          text="Overdue"
                        />
                      ) : props.dataItem.IsNew && (
                        <FluentBadge
                          shape="rounded"
                          appearance="filled"
                          size="medium"
                          color="success"
                          text="New"
                        />
                      )}
                      <div style={{ pointerEvents: "auto" }}>
                        <EditIconButton
                          projectId={props.dataItem.ProjectId}
                          projectName={props.dataItem.ProjectName}
                          disabled={!canEdit || !(props.dataItem.Members || []).some((m) => m.User?.Id === user?.userID)}
                          tooltipContent={
                            !canEdit || !(props.dataItem.Members || []).some((m) => m.User?.Id === user?.userID) ? (
                              <span>You are not a Member of this Deal, therefore you cannot Edit it. Reach out to another Staffer or a Deal Member to get added to this Deal.</span>
                            ) : undefined
                          }
                        />
                      </div>
                    </div>
                  </td>
                )
              ) : (
                <td colSpan={9} className="k-table-td">
                  <TechStafferNoDealsEmptyState isStaffer={isStaffer} />
                </td>
              )
            }
          />
          <Column
            field="ProjectName"
            title={renderTooltipHeader("Deal Name")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <FluentTooltip
                  target={
                    <td
                      data-testid="project-name-column"
                      className="background-white k-table-td"
                    >
                      <div className="project-name-wrapper">
                        <span className="project-name-text">{props.dataItem.ProjectName}</span>
                      </div>
                    </td>
                  }
                  text={props.dataItem.ProjectName}
                />
              )
            }
          />
          <Column
            field="LastWeekHours"
            title={renderTooltipHeader("Last Wk Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.LastWeekHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="WeekendHours"
            title={renderTooltipHeader("Last Wknd Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.WeekendHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="TotalHours"
            title={renderTooltipHeader("Total Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("TotalHours") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.TotalHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="CurrentWeekHours"
            title={renderTooltipHeader("Current Wk Est")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.CurrentWeekHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="Members"
            title={renderTooltipHeader("Deal Team")}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="background-white k-table-td">
                  {props.dataItem.Members?.length ? (
                    <TechDealTeamAvatarGroupCell members={props.dataItem.Members} />
                  ) : (
                    "–"
                  )}
                </td>
              )
            }
          />
          <Column
            field="SeniorMembers"
            title={renderLabelWithInfoTooltipHeader(
              "MDs/Directors/VPs",
              "Senior members associated with the deal"
            )}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("SeniorMembers") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="background-white k-table-td">
                  <SeniorMembersCell names={props.dataItem.SeniorMemberNamesArray} />
                </td>
              )
            }
          />
          <Column
            field="LastUpdated"
            title={renderlastUpdatedTooltipHeader("Last Updated")}
            columnMenu={columnMenu}
            filter="date"
            headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
            cell={(props) =>
              techProjects?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td
                  className="background-white k-table-td"
                  style={{
                    color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated),
                  }}
                >
                  <span style={{ pointerEvents: "none" }}>
                    {formatLastUpdatedDate(props.dataItem.LastUpdated)}
                  </span>
                </td>
              )
            }
          />
        </Grid>
      </div>
      {showDealModal && selectedDeal && (
        <TechStafferDealTimeLogModal
          show={showDealModal}
          onHide={() => setShowDealModal(false)}
          selectedDeal={selectedDeal}
        />
      )}
      <DealWidgetExcelExport data={filteredData} ref={_exportRef} />
    </div>
  );
};

TechStafferDealTimeLog.propTypes = {
  techProjects: PropTypes.array,
  isStaffer: PropTypes.bool,
  subTitle: PropTypes.string,
};

export default TechStafferDealTimeLog;
